/**
 * tree.utils.ts — H4 fix: extracted from TreeViewPage.tsx
 *
 * Shared tree utilities so changes to TreeNode shape only need to happen
 * in one place and don't silently break tree callbacks.
 */

import React from 'react';
import { Target, CheckCircle2, Dot } from 'lucide-react';
import type { TreeNode as UITreeNode } from '@/components/ui/TreeView/TreeView';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/localeFormatters';
import type { TreeNode } from '@/types/roros/tree';

// ─── buildNodeLabel ───────────────────────────────────────────────────────────

/**
 * Build the display label for a tree node.
 *
 * Goals:    "<title> (<Domain> | Target date: <date> | Accountable: <name>)"
 * Outcomes: "<title> (<Domain> | Accountable: <name>)"
 * Others:   "<title>" (unchanged)
 *
 * Any segment whose value is missing is silently skipped.
 * When all metadata segments are empty, no parentheses are appended.
 *
 * Exported for unit testing (allows translate/formatDateFn injection).
 */
export function buildNodeLabel(
  node: Pick<TreeNode, 'type' | 'title' | 'domainId' | 'targetDate' | 'accountable'>,
  domainsById: Map<string, string>,
  options: {
    translate?: (key: string) => string;
    formatDateFn?: (d: string | null | undefined) => string;
  } = {},
): string {
  const translate = options.translate ?? t;
  const formatDateFn = options.formatDateFn ?? formatDate;

  const title = node.title || translate('(untitled)');

  if (node.type !== 'goal' && node.type !== 'outcome') {
    return title;
  }

  const segments: string[] = [];

  // Domain segment
  if (node.domainId) {
    const domainName = domainsById.get(node.domainId);
    if (domainName) segments.push(domainName);
  }

  // Target date (goals only)
  if (node.type === 'goal' && node.targetDate) {
    const formatted = formatDateFn(node.targetDate);
    if (formatted) segments.push(`${translate('Target date')}: ${formatted}`);
  }

  // Accountable
  if (node.accountable?.name) {
    segments.push(`${translate('Accountable')}: ${node.accountable.name}`);
  }

  if (segments.length === 0) return title;
  return `${title} (${segments.join(' | ')})`;
}

// ─── toUINode ─────────────────────────────────────────────────────────────────

/**
 * Convert a TreeNode from the API into the UITreeNode shape for the TreeView
 * component. Recursively converts children.
 *
 * @param node         The API tree node
 * @param onGoalClick  Click handler receiving the node id
 * @param onOutcomeClick
 * @param onWorkItemClick
 * @param domainsById  Map of domainId → domain name for label metadata
 */
export function toUINode(
  node: TreeNode,
  onGoalClick: (id: string) => void,
  onOutcomeClick: (id: string) => void,
  onWorkItemClick: (id: string) => void,
  domainsById: Map<string, string> = new Map(),
): UITreeNode {
  let icon: React.ReactNode;
  if (node.type === 'goal') {
    icon = (
      React.createElement('span', { className: 'flex items-center gap-1' },
        React.createElement(Target, { className: 'w-4 h-4 text-amber-500', 'aria-hidden': 'true' }),
      )
    );
  } else if (node.type === 'outcome') {
    icon = (
      React.createElement('span', { className: 'flex items-center gap-1' },
        React.createElement(CheckCircle2, { className: 'w-4 h-4 text-blue-500', 'aria-hidden': 'true' }),
      )
    );
  } else if (node.type === 'work_item') {
    icon = (
      React.createElement('span', { className: 'flex items-center gap-1' },
        React.createElement(Dot, { className: 'w-4 h-4 text-muted-foreground', 'aria-hidden': 'true' }),
      )
    );
  }

  const uiNode: UITreeNode = {
    id: node.id,
    label: buildNodeLabel(node, domainsById),
    icon,
    data: { type: node.type, nodeData: node },
    children: node.children.map((child) =>
      toUINode(child, onGoalClick, onOutcomeClick, onWorkItemClick, domainsById),
    ),
  };

  return uiNode;
}

// ─── findNodeById ─────────────────────────────────────────────────────────────

/**
 * BFS search for a node by id in the tree.
 * Returns null if no match is found.
 */
export function findNodeById(root: TreeNode, id: string): TreeNode | null {
  const queue: TreeNode[] = [root];
  while (queue.length > 0) {
    const node = queue.shift()!;
    if (node.id === id) return node;
    for (const child of node.children) {
      queue.push(child);
    }
  }
  return null;
}
